export type Locale = 'en' | 'pt-BR';

type TranslationMap = {
  nav: Record<string, string>;
  footer: Record<string, string>;
  home: Record<string, string>;
  auth: Record<string, string>;
  download: Record<string, string>;
  usercp: Record<string, string>;
  store: Record<string, string>;
  promo: Record<string, string>;
  rankings: Record<string, string>;
  streamers: Record<string, string>;
  news: Record<string, string>;
  classes: Record<string, string>;
  common: Record<string, string>;
};

function flatten(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, flatten(v as Record<string, unknown>, key));
    } else if (typeof v === 'string') {
      out[key] = v;
    }
  }
  return out;
}

const en: TranslationMap = {
  nav: {
    home: 'HOME',
    game: 'GAME',
    news: 'NEWS',
    community: 'COMMUNITY',
    donate: 'DONATE',
    classes: 'CLASSES',
    rankings: 'RANKINGS',
    discord: 'DISCORD',
    download: 'DOWNLOAD',
    admin: 'ADMIN',
    myAccount: 'MY ACCOUNT',
    logout: 'LOGOUT',
    login: 'LOGIN',
    register: 'REGISTER',
    playNow: 'PLAY NOW',
  },
  footer: {
    links: 'LINKS',
    rankings: 'Rankings',
    news: 'News',
    guide: 'Guide',
    support: 'Support',
    kvkk: 'K.V.K.K',
    newsAndAnnouncements: 'NEWS AND ANNOUNCEMENTS',
    patchNotes: 'Patch Notes',
    developments: 'Developments',
    events: 'Events',
    announcements: 'Announcements',
    memberProcedures: 'MEMBER PROCEDURES',
    newMemberRegistration: 'New Member Registration',
    signIn: 'Sign In',
    forgotPassword: 'I forgot my password',
    helpSupport: 'HELP / SUPPORT',
    faq: 'Frequently Asked Questions',
    forum: 'Forum',
    discordSupport: 'Discord Support',
    createSupportTicket: 'Create Support Ticket',
    copyright: 'Knight Icarus Online - Copyright © {year}. All Rights Reserved.',
  },
  home: {
    rideTheSkies: 'MORE THAN A GAME.',
    heroTitle: 'A CELESTIAL',
    heroTitleHighlight: 'JOURNEY.',
    heroDesc: 'Fight for the light, protect the realms and become a legend in Knight Icarus Online.',
    playNow: 'Play Now',
    learnMore: 'Learn More',
    scrollDown: 'Scroll Down',
    subscribeNow: 'Create Account',
    exploreWorld: 'EXPLORE THE WORLD',
    shortcutGuides: 'Guides',
    shortcutMounts: 'Mounts',
    shortcutClasses: 'Classes',
    shortcutRankings: 'Rankings',
    newsEvents: 'NEWS & EVENTS',
    countdownLabel: 'Grand Opening',
    countdownDays: 'Days',
    countdownHours: 'Hours',
    countdownMinutes: 'Min',
    countdownSeconds: 'Sec',
    countdownLive: 'Server is live!',
    ctaTitle: 'Ready to take flight?',
    ctaDesc: 'Download the client, create your account and begin your adventure in the skies of Icarus.',
    downloadGame: 'Download Game',
    createAccount: 'Create Account',
    latestNews: 'LATEST',
    latestNewsHighlight: 'NEWS',
    viewAll: 'View All',
    readMore: 'Read More →',
    noNews: 'No news published yet.',
    gameFeatures: 'GAME',
    gameFeaturesHighlight: 'FEATURES',
    feature1Title: 'Tame & Ride Legendary Beasts',
    feature1Desc: 'Capture wild familiars in the field, raise their loyalty, then contract them as flying mounts, ground mounts, or combat pets. Only Riders who prove themselves can tame the rarest dragons.',
    feature2Title: 'Combat on Mount Back',
    feature2Desc: 'Fight while mounted: use mount skills in mid-air and on the ground. Aerial PvP and PvE where positioning and mount choice define the outcome of battles.',
    feature3Title: 'Hakanas & the World of Icarus',
    feature3Desc: 'Start in the capital Hakanas, then explore zones like Ellora, Lephantis, and the outer realms. Each region has its own taming targets, dungeons, and world bosses.',
    feature4Title: '8 Classes: Assassin to Priest',
    feature4Desc: 'Assassin, Archer, Mage, Berserker, Guardian, Trickster, Magician, Priest. Each has distinct skills and roles in party dungeons and PvP. Pick your style and master it.',
    feature5Title: 'Instance Dungeons & Raids',
    feature5Desc: 'Enter instanced dungeons with your party, clear bosses, and earn gear and materials. Progress from normal to hard modes and compete on clear times and rankings.',
    feature6Title: 'Guilds & Guild Content',
    feature6Desc: 'Create or join a guild for guild quests, guild wars, and shared goals. Conquer territory and face guild-exclusive challenges with your allies.',
    feature7Title: 'Rankings & Season Rewards',
    feature7Desc: 'Climb PvP and PvE rankings for seasonal rewards. Top Riders earn titles, cosmetics, and exclusive items. Rankings reset by season so the competition stays fresh.',
    feature8Title: 'Cash Shop & Character Customization',
    feature8Desc: 'Customize your Rider with costumes, mount skins, and convenience items from the in-game cash shop. Stand out in the skies and in town.',
  },
  auth: {
    login: 'LOGIN',
    loginSubtitle: 'Enter your credentials to access your account',
    username: 'Username',
    password: 'Password',
    enterUsername: 'Enter your username',
    enterPassword: 'Enter your password',
    loggingIn: 'Logging in...',
    noAccount: "Don't have an account?",
    registerHere: 'Register here',
    register: 'REGISTER',
    registerSubtitle: 'Create a new account to start your adventure',
    email: 'Email',
    enterEmail: 'Enter your email address',
    confirmPassword: 'Confirm Password',
    chooseUsername: 'Choose a username (3-64 characters)',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordMinLength: 'Password must be at least 6 characters',
    usernameReserved: 'This username is not allowed',
    usernameTaken: 'This username is already in use',
    emailTaken: 'This email is already registered',
    registerError: 'An error occurred during registration',
    creatingAccount: 'Creating account...',
    registerSuccessTitle: 'Registration Complete',
    registerSuccessMessage: 'Your account has been created successfully.',
    registerSuccessDetail: 'You can now log in and start your adventure.',
    haveAccount: 'Already have an account?',
    loginHere: 'Login here',
    enterPasswordMin: 'Enter your password (min 6 characters)',
    confirmPasswordPlaceholder: 'Confirm your password',
    forgotPassword: 'Forgot Password',
    forgotSubtitle: 'Enter your email to receive a password reset link',
    sendResetLink: 'Send Reset Link',
    backToLogin: 'Back to Login',
    forgotSuccess: 'Password recovery instructions have been sent to your email address.',
    forgotError: 'An error occurred while sending recovery instructions.',
    emailAddress: 'Email Address',
    rememberPassword: 'Remember your password?',
    sending: 'Sending...',
    sendRecoveryEmail: 'SEND RECOVERY EMAIL',
  },
  download: {
    free: 'FREE',
    title: 'DOWNLOAD THE GAME NOW',
    subtitle: 'Knight Icarus Online Client',
    clientDesc: 'A next-generation graphics and performance experience. Choose an option below.',
    loading: 'Loading options...',
    downloadGameDefault: 'DOWNLOAD GAME Knight Icarus Online - New Client',
    systemRequirements: 'SYSTEM REQUIREMENTS',
    systemRequirementsNote: 'Requires a 64-bit processor and operating system.',
    minimum: 'Minimum',
    suggested: 'Suggested',
    lowSettings: 'Low Settings',
    highQuality: 'High Quality',
    operatingSystem: 'Operating System',
    processor: 'Processor',
    ram: 'Ram',
    gpu: 'Gpu',
    directX: 'DirectX',
    storage: 'Storage',
  },
  usercp: {
    loading: 'Loading user data...',
    menu: 'MENU',
    accountManagement: 'ACCOUNT MANAGEMENT',
    overview: 'Overview',
    editProfile: 'Edit Profile',
    security: 'Security (2FA)',
    changePassword: 'Change Password',
    services: 'SERVICES',
    myCharacters: 'My Characters',
    eventPromo: 'Event Promo',
    eventPromoDesc: 'Enter an event promotional code to receive exclusive items on the character you choose.',
    store: 'Store',
    supportLabel: 'SUPPORT',
    supportRequests: 'Support Requests',
    accountHistory: 'Account History',
    logOut: 'Log Out',
    welcomeBanner: 'WELCOME TO THE CONTROL PANEL',
    hello: 'Hello,',
    welcomeDesc: 'You can access all information about your account here, view your transaction history, and manage your settings.',
    yourBalance: 'Your balance',
    securityLevel: 'Security Level',
    securityLevelValue: 'Middle (Mail)',
    lastLogin: 'Last Login',
    quickActions: 'Quick Actions',
    viewAll: 'View All',
    reviewProducts: 'Review the products',
    profile: 'Profile',
    editYourInfo: 'Edit your information',
    securitySettings: '2FA Settings',
    getSupport: 'Get Support',
    accountSummary: 'ACCOUNT SUMMARY',
    registrationDate: 'Registration Date',
    emailStatus: 'E-mail Status',
    approved: 'APPROVED',
    editProfileTitle: 'Edit Profile',
    editProfileDesc: 'Update your account information and preferences.',
    active: 'ACTIVE',
    characterDetails: 'Character Details',
    lastLoginChar: 'Last Login',
    noCharacters: 'No characters found.',
    myCharactersTitle: 'My Characters',
    myCharactersDesc: 'View and manage your game characters.',
    level: 'Level',
    online: 'Online',
    offline: 'Offline',
    race: 'Race',
    gender: 'Gender',
    location: 'Location',
    money: 'Money',
    gold: 'Gold',
    noCharactersHint: 'Start playing to create your first character!',
  },
  promo: {
    title: 'Event Package Promo',
    desc: 'Enter a promotional code to receive an exclusive Event Package. One redemption per account.',
    codePlaceholder: 'Enter your promo code',
    validate: 'Validate',
    validating: 'Checking...',
    codeInvalid: 'Invalid or expired promotional code.',
    chooseClass: 'Choose a class:',
    chooseChar: 'Choose the character who will receive the items:',
    redeem: 'Redeem',
    redeeming: 'Redeeming...',
    noChars: 'Create a character in-game to receive the package.',
    loginNeeded: 'Log in to redeem this package.',
    login: 'Log in',
    alreadyTitle: 'Event package already redeemed',
    alreadyDesc: 'This account has already redeemed an event package. The items were sent to the chosen character.',
    alreadyStatus: 'Redeemed on',
    allDoneTitle: 'All event boxes of this package have been redeemed',
    redeemedClasses: 'redeemed classes',
    redeemed: 'Redeemed',
    goPanel: 'Go to my panel',
    error: 'Could not redeem the package.',
  },
  store: {
    title: 'Cash Shop',
    yourBalance: 'Your balance',
    items: 'Items',
    buy: 'Buy',
    selectCharacter: 'Select character',
    selectCharacterOption: '— Choose a character —',
    quantity: 'Quantity',
    outbox: 'Inbox (cash items)',
    outboxDesc: 'Items purchased here are delivered to your character\'s inbox in-game.',
    outboxSelectHint: 'Choose which character to view inbox:',
    outboxChoosePrompt: 'Select a character above to see items delivered to them in-game.',
    outboxEmpty: 'No items in this character\'s inbox.',
    outboxNoCharacters: 'Create a character in-game to receive and view items here.',
    noItems: 'No items in the shop.',
    noCharacters: 'Create a character in-game to receive items.',
    buySuccess: 'Purchase successful. Item sent to character inbox.',
    buyError: 'Purchase failed.',
    loading: 'Loading...',
    loginRequired: 'Please log in to use the store.',
    allCategories: 'All',
    prevPage: 'Previous',
    nextPage: 'Next',
  },
  rankings: {
    tower: 'Infinite Tower',
    pk: 'PvP',
    guild: 'Guilds',
    rank: 'Rank',
    name: 'Name',
    class: 'Class',
    level: 'Level',
    score: 'Score',
    guildName: 'Guild',
    loadErrorTower: 'Failed to load Tower ranking.',
    loadErrorPk: 'Failed to load PvP ranking.',
    loadErrorGuild: 'Failed to load Guild ranking.',
    loadErrorLevel: 'Failed to load Level ranking.',
    noData: 'No data available.',
    loading: 'Loading...',
    floor: 'Floor (Clear)',
    points: 'Points',
    grade: 'Grade',
    exploits: 'Exploits',
    master: 'Master',
  },
  streamers: {
    title: 'LIVES',
    titleHighlight: 'NOW',
    subtitle: 'Follow our streamers and watch the action live.',
    liveNow: 'AO VIVO',
    liveOn: 'LIVE ON',
    live1: 'Watch Live 1',
    live2: 'Watch Live 2',
    watching: 'Streaming now',
    watchNow: 'Watch now',
    noLive: 'No one online right now',
    noLiveDesc: 'Check back soon — our streamers will be back.',
    offline: 'Offline',
    viewers: '{count} watching',
  },
  news: {
    allNews: 'All News',
    readMore: 'Read more',
    title: 'NEWS &',
    titleHighlight: 'UPDATES',
    readDetails: 'Read the details →',
    noNews: 'No news published yet.',
  },
  classes: {
    title: 'Classes',
    guide: 'Class Guide',
  },
  common: {
    cash: 'Cash',
  },
};

const ptBR: TranslationMap = {
  nav: {
    home: 'INÍCIO',
    game: 'JOGO',
    news: 'NOTÍCIAS',
    community: 'COMUNIDADE',
    donate: 'DOAÇÃO',
    classes: 'CLASSES',
    rankings: 'RANKINGS',
    discord: 'DISCORD',
    download: 'DOWNLOAD',
    admin: 'ADMIN',
    myAccount: 'MINHA CONTA',
    logout: 'SAIR',
    login: 'ENTRAR',
    register: 'CADASTRAR',
    playNow: 'JOGAR AGORA',
  },
  footer: {
    links: 'LINKS',
    rankings: 'Rankings',
    news: 'Notícias',
    guide: 'Guia',
    support: 'Suporte',
    kvkk: 'K.V.K.K',
    newsAndAnnouncements: 'NOTÍCIAS E AVISOS',
    patchNotes: 'Patch Notes',
    developments: 'Desenvolvimentos',
    events: 'Eventos',
    announcements: 'Avisos',
    memberProcedures: 'PROCEDIMENTOS DE MEMBRO',
    newMemberRegistration: 'Novo cadastro',
    signIn: 'Entrar',
    forgotPassword: 'Esqueci minha senha',
    helpSupport: 'AJUDA / SUPORTE',
    faq: 'Perguntas frequentes',
    forum: 'Fórum',
    discordSupport: 'Suporte no Discord',
    createSupportTicket: 'Criar ticket de suporte',
    copyright: 'Knight Icarus Online - Copyright © {year}. Todos os direitos reservados.',
  },
  home: {
    rideTheSkies: 'MAIS QUE UM JOGO.',
    heroTitle: 'UMA JORNADA',
    heroTitleHighlight: 'CELESTIAL.',
    heroDesc: 'Lute pela luz, proteja os reinos e torne-se uma lenda no Knight Icarus Online.',
    playNow: 'Jogar Agora',
    learnMore: 'Saiba Mais',
    scrollDown: 'Rolar',
    subscribeNow: 'Criar Conta',
    exploreWorld: 'EXPLORE O MUNDO',
    shortcutGuides: 'Guias',
    shortcutMounts: 'Montarias',
    shortcutClasses: 'Classes',
    shortcutRankings: 'Rankings',
    newsEvents: 'NOTÍCIAS & EVENTOS',
    countdownLabel: 'Grande Abertura',
    countdownDays: 'Dias',
    countdownHours: 'Horas',
    countdownMinutes: 'Min',
    countdownSeconds: 'Seg',
    countdownLive: 'Servidor no ar!',
    ctaTitle: 'Pronto para voar?',
    ctaDesc: 'Baixe o cliente, crie sua conta e comece sua aventura nos céus de Icarus.',
    downloadGame: 'Baixar jogo',
    createAccount: 'Criar conta',
    latestNews: 'ÚLTIMAS',
    latestNewsHighlight: 'NOTÍCIAS',
    viewAll: 'Ver todas',
    readMore: 'Leia mais →',
    noNews: 'Nenhuma notícia publicada ainda.',
    gameFeatures: 'RECURSOS',
    gameFeaturesHighlight: 'DO JOGO',
    feature1Title: 'Domar e montar bestas lendárias',
    feature1Desc: 'Capture famílias selvagens no campo, aumente a lealdade e contrate-as como montarias voadoras, terrestres ou mascotes de combate. Apenas Cavaleiros que se provarem podem domar os dragões mais raros.',
    feature2Title: 'Combate em montaria',
    feature2Desc: 'Lute montado: use habilidades da montaria no ar e no chão. PvP e PvE aéreos onde posicionamento e escolha da montaria definem o resultado das batalhas.',
    feature3Title: 'Hakanas e o mundo de Icarus',
    feature3Desc: 'Comece na capital Hakanas e explore zonas como Ellora, Lephantis e os reinos exteriores. Cada região tem seus alvos de domesticação, masmorras e chefes de mundo.',
    feature4Title: '8 classes: Assassino a Sacerdote',
    feature4Desc: 'Assassino, Arqueiro, Mago, Berserker, Guardião, Trickster, Magician, Sacerdote. Cada um com habilidades e funções distintas em masmorras em grupo e PvP.',
    feature5Title: 'Masmorras e raids instanciadas',
    feature5Desc: 'Entre em masmorras instanciadas com seu grupo, derrote chefes e ganhe equipamentos e materiais. Avance do modo normal ao difícil e compita em tempo e rankings.',
    feature6Title: 'Guildas e conteúdo de guilda',
    feature6Desc: 'Crie ou entre em uma guilda para missões, guerras e objetivos compartilhados. Conquiste território e enfrente desafios exclusivos de guilda com seus aliados.',
    feature7Title: 'Rankings e recompensas de temporada',
    feature7Desc: 'Suba nos rankings PvP e PvE para recompensas de temporada. Os melhores Cavaleiros ganham títulos, cosméticos e itens exclusivos.',
    feature8Title: 'Loja e customização de personagem',
    feature8Desc: 'Personalize seu Cavaleiro com roupas, skins de montaria e itens de conveniência na loja do jogo. Destaque-se nos céus e na cidade.',
  },
  auth: {
    login: 'ENTRAR',
    loginSubtitle: 'Digite suas credenciais para acessar sua conta',
    username: 'Usuário',
    password: 'Senha',
    enterUsername: 'Digite seu usuário',
    enterPassword: 'Digite sua senha',
    loggingIn: 'Entrando...',
    noAccount: 'Não tem uma conta?',
    registerHere: 'Cadastre-se aqui',
    register: 'CADASTRAR',
    registerSubtitle: 'Crie uma nova conta para começar sua aventura',
    email: 'E-mail',
    enterEmail: 'Digite seu e-mail',
    confirmPassword: 'Confirmar senha',
    chooseUsername: 'Escolha um usuário (3-64 caracteres)',
    passwordsDoNotMatch: 'As senhas não coincidem',
    passwordMinLength: 'A senha deve ter pelo menos 6 caracteres',
    usernameReserved: 'Este nome de usuário não é permitido',
    usernameTaken: 'Este nome de usuário já está em uso',
    emailTaken: 'Este e-mail já está cadastrado',
    registerError: 'Ocorreu um erro ao criar a conta',
    creatingAccount: 'Criando conta...',
    registerSuccessTitle: 'Cadastro concluído',
    registerSuccessMessage: 'Sua conta foi criada com sucesso.',
    registerSuccessDetail: 'Agora você pode fazer login e começar sua aventura.',
    haveAccount: 'Já tem uma conta?',
    loginHere: 'Entrar aqui',
    enterPasswordMin: 'Digite sua senha (mín. 6 caracteres)',
    confirmPasswordPlaceholder: 'Confirme sua senha',
    forgotPassword: 'Esqueci a senha',
    forgotSubtitle: 'Digite seu e-mail para receber o link de redefinição',
    sendResetLink: 'Enviar link',
    backToLogin: 'Voltar ao login',
    forgotSuccess: 'Instruções de recuperação de senha foram enviadas para seu e-mail.',
    forgotError: 'Ocorreu um erro ao enviar as instruções de recuperação.',
    emailAddress: 'E-mail',
    rememberPassword: 'Lembrou sua senha?',
    sending: 'Enviando...',
    sendRecoveryEmail: 'ENVIAR E-MAIL DE RECUPERAÇÃO',
  },
  download: {
    free: 'GRÁTIS',
    title: 'BAIXE O JOGO AGORA',
    subtitle: 'Cliente Knight Icarus Online',
    clientDesc: 'Experiência de gráficos e desempenho de próxima geração. Escolha uma opção abaixo.',
    loading: 'Carregando opções...',
    downloadGameDefault: 'BAIXAR JOGO Knight Icarus Online - Novo cliente',
    systemRequirements: 'REQUISITOS DO SISTEMA',
    systemRequirementsNote: 'Requer processador e sistema operacional de 64 bits.',
    minimum: 'Mínimo',
    suggested: 'Recomendado',
    lowSettings: 'Configurações baixas',
    highQuality: 'Alta qualidade',
    operatingSystem: 'Sistema operacional',
    processor: 'Processador',
    ram: 'Memória RAM',
    gpu: 'Placa de vídeo',
    directX: 'DirectX',
    storage: 'Armazenamento',
  },
  usercp: {
    loading: 'Carregando dados do usuário...',
    menu: 'MENU',
    accountManagement: 'GERENCIAMENTO DA CONTA',
    overview: 'Visão geral',
    editProfile: 'Editar perfil',
    security: 'Segurança (2FA)',
    changePassword: 'Alterar senha',
    services: 'SERVIÇOS',
    myCharacters: 'Meus personagens',
    eventPromo: 'Promo de Evento',
    eventPromoDesc: 'Digite um código promocional de evento para receber itens exclusivos no personagem que você escolher.',
    store: 'Loja',
    supportLabel: 'SUPORTE',
    supportRequests: 'Solicitações de suporte',
    accountHistory: 'Histórico da conta',
    logOut: 'Sair',
    welcomeBanner: 'BEM-VINDO AO PAINEL DE CONTROLE',
    hello: 'Olá,',
    welcomeDesc: 'Você pode acessar todas as informações da sua conta aqui, ver seu histórico de transações e gerenciar suas configurações.',
    yourBalance: 'Seu saldo',
    securityLevel: 'Nível de segurança',
    securityLevelValue: 'Médio (E-mail)',
    lastLogin: 'Último acesso',
    quickActions: 'Acesso rápido',
    viewAll: 'Ver tudo',
    reviewProducts: 'Ver produtos',
    profile: 'Perfil',
    editYourInfo: 'Editar suas informações',
    securitySettings: 'Configurações 2FA',
    getSupport: 'Obter suporte',
    accountSummary: 'RESUMO DA CONTA',
    registrationDate: 'Data de cadastro',
    emailStatus: 'Status do e-mail',
    approved: 'APROVADO',
    editProfileTitle: 'Editar perfil',
    editProfileDesc: 'Atualize suas informações e preferências da conta.',
    active: 'ATIVO',
    characterDetails: 'Detalhes do personagem',
    lastLoginChar: 'Último acesso',
    noCharacters: 'Nenhum personagem encontrado.',
    myCharactersTitle: 'Meus personagens',
    myCharactersDesc: 'Visualize e gerencie seus personagens do jogo.',
    level: 'Nível',
    online: 'Online',
    offline: 'Offline',
    race: 'Raça',
    gender: 'Gênero',
    location: 'Localização',
    money: 'Dinheiro',
    gold: 'Gold',
    noCharactersHint: 'Jogue para criar seu primeiro personagem!',
  },
  promo: {
    title: 'Promo de Pacote de Evento',
    desc: 'Digite um código promocional para receber um Pacote de Evento exclusivo. Apenas 1 resgate por conta.',
    codePlaceholder: 'Digite seu código promocional',
    validate: 'Validar',
    validating: 'Verificando...',
    codeInvalid: 'Código promocional inválido ou expirado.',
    chooseClass: 'Escolha uma classe:',
    chooseChar: 'Escolha o personagem que receberá os itens:',
    redeem: 'Resgatar',
    redeeming: 'Resgatando...',
    noChars: 'Crie um personagem no jogo para receber o pacote.',
    loginNeeded: 'Entre na sua conta para resgatar este pacote.',
    login: 'Entrar',
    alreadyTitle: 'Pacote de evento já resgatado',
    alreadyDesc: 'Esta conta já resgatou um pacote de evento. Os itens foram enviados para o personagem escolhido.',
    alreadyStatus: 'Resgatado em',
    allDoneTitle: 'Todas as caixas deste pacote já foram resgatadas',
    redeemedClasses: 'classes resgatadas',
    redeemed: 'Resgatado',
    goPanel: 'Ir para meu painel',
    error: 'Não foi possível resgatar o pacote.',
  },
  store: {
    title: 'Loja de Cash',
    yourBalance: 'Seu saldo',
    items: 'Itens',
    buy: 'Comprar',
    selectCharacter: 'Selecionar personagem',
    selectCharacterOption: '— Escolha um personagem —',
    quantity: 'Quantidade',
    outbox: 'Caixa de correio (itens cash)',
    outboxDesc: 'Itens comprados aqui são enviados para a caixa de correio do personagem no jogo.',
    outboxSelectHint: 'Escolha o personagem para ver a caixa de correio:',
    outboxChoosePrompt: 'Selecione um personagem acima para ver os itens enviados a ele no jogo.',
    outboxEmpty: 'Nenhum item na caixa deste personagem.',
    outboxNoCharacters: 'Crie um personagem no jogo para receber e ver os itens aqui.',
    noItems: 'Nenhum item na loja.',
    noCharacters: 'Crie um personagem no jogo para receber os itens.',
    buySuccess: 'Compra realizada. Item enviado para a caixa do personagem.',
    buyError: 'Falha na compra.',
    loading: 'Carregando...',
    loginRequired: 'Faça login para usar a loja.',
    allCategories: 'Todos',
    prevPage: 'Anterior',
    nextPage: 'Seguinte',
  },
  rankings: {
    tower: 'Torre Infinita',
    pk: 'PvP',
    guild: 'Guildas',
    rank: 'Pos.',
    name: 'Nome',
    class: 'Classe',
    level: 'Nível',
    score: 'Pontuação',
    guildName: 'Guilda',
    loadErrorTower: 'Falha ao carregar ranking da Torre.',
    loadErrorPk: 'Falha ao carregar ranking PvP.',
    loadErrorGuild: 'Falha ao carregar ranking de Guilds.',
    loadErrorLevel: 'Falha ao carregar ranking de Nível.',
    noData: 'Nenhum dado disponível.',
    loading: 'Carregando...',
    floor: 'Andar (Clear)',
    points: 'Pontos',
    grade: 'Grau',
    exploits: 'Exploits',
    master: 'Mestre',
  },
  streamers: {
    title: 'LIVES',
    titleHighlight: 'AO VIVO',
    subtitle: 'Acompanhe nossos streamers e assista a gameplay ao vivo.',
    liveNow: 'AO VIVO',
    liveOn: 'AO VIVO',
    live1: 'Assistir Live 1',
    live2: 'Assistir Live 2',
    watching: 'Transmitindo agora',
    watchNow: 'Assistir agora',
    noLive: 'Ninguém online no momento',
    noLiveDesc: 'Volte mais tarde — nossos streamers estarão por aqui.',
    offline: 'Offline',
    viewers: '{count} assistindo',
  },
  news: {
    allNews: 'Todas as notícias',
    readMore: 'Leia mais',
    title: 'NOTÍCIAS E',
    titleHighlight: 'ATUALIZAÇÕES',
    readDetails: 'Ver detalhes →',
    noNews: 'Nenhuma notícia publicada ainda.',
  },
  classes: {
    title: 'Classes',
    guide: 'Guia de classes',
  },
  common: {
    cash: 'Cash',
  },
};

const flatEn = flatten(en as unknown as Record<string, unknown>);
const flatPtBR = flatten(ptBR as unknown as Record<string, unknown>);

export const translations: Record<Locale, Record<string, string>> = {
  en: flatEn,
  'pt-BR': flatPtBR,
};

export function getTranslation(locale: Locale, key: string): string {
  const dict = translations[locale];
  return dict[key] ?? translations.en[key] ?? key;
}
